(() => {
  if (customElements.get('tup-process')) return;

  class TupProcess extends HTMLElement {
    static get observedAttributes() { return ['data-motion']; }

    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      this.video = this.querySelector('.tup-film__video');
      this.stage = this.querySelector('[data-film-stage]');
      this.track = this.querySelector('[data-film-track]');
      this.steps = [...this.querySelectorAll('[data-process-step]')];
      this.chapterLinks = [...this.querySelectorAll('[data-film-chapter]')];
      this.toggle = this.querySelector('[data-film-toggle]');
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.progress = 0;
      this.target = 0;
      this.reading = false;
      this.failed = false;
      this.near = false;
      this.sourceLoaded = false;
      this.seekable = false;
      this.blobAttempted = false;
      if (!this.video || !this.stage || !this.steps.length) return;
      const options = { signal: this.lifecycle.signal };
      this.video.muted = true;
      this.video.playsInline = true;
      this.video.tabIndex = -1;
      this.video.setAttribute('aria-hidden', 'true');
      this.video.addEventListener('loadedmetadata', () => this.prepare(), options);
      this.video.addEventListener('durationchange', () => this.prepare(), options);
      this.video.addEventListener('loadeddata', () => { this.prepare(); this.frameReady(); }, options);
      this.video.addEventListener('progress', () => this.prepare(), options);
      this.video.addEventListener('canplay', () => this.prepare(), options);
      this.video.addEventListener('seeked', () => { this.frameReady(); this.schedule(); }, options);
      this.video.addEventListener('error', () => { if (this.sourceLoaded) this.fail(); }, options);
      const sources = [...this.video.querySelectorAll('source')];
      let failedSources = 0;
      sources.forEach((source) => source.addEventListener('error', () => {
        if (!this.sourceLoaded) return;
        failedSources += 1;
        if (failedSources >= sources.length) this.fail();
      }, options));
      this.motionPreference.addEventListener('change', () => this.configure(), options);
      this.connection?.addEventListener?.('change', () => this.configure(), options);
      window.addEventListener('scroll', () => this.schedule(), { ...options, passive: true });
      window.addEventListener('resize', () => { this.measureStage(); this.schedule(); }, options);
      this.chapterLinks.forEach((link, index) => link.addEventListener('click', (event) => {
        if (!this.hasAttribute('data-enhanced')) return;
        event.preventDefault();
        this.jumpTo(index, 'smooth');
      }, options));
      this.toggle?.addEventListener('click', () => {
        this.reading = !this.reading;
        this.configure();
        this.scrollIntoView({ block: 'start', behavior: 'auto' });
      }, options);
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.steps.indexOf(event.target.closest('[data-process-step]'));
        if (index >= 0 && this.hasAttribute('data-enhanced')) this.jumpTo(index, 'auto');
      }, options);
      if ('ResizeObserver' in window) {
        this.sizeObserver = new ResizeObserver(() => { this.measureStage(); this.schedule(); });
        this.sizeObserver.observe(this.stage);
      }
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          this.near = entries.some((entry) => entry.isIntersecting);
          if (this.near) { this.ensureSource(); this.ensureSeekableSource(); this.schedule(); }
          else this.stop();
        }, { rootMargin: '350px 0px' });
        this.visibilityObserver.observe(this.stage);
      } else this.near = true;
      this.configure();
      this.prepare();
    }

    disconnectedCallback() {
      this.stop();
      this.lifecycle?.abort();
      this.lifecycle = null;
      this.sizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.video?.pause();
      this.releaseBlob();
    }

    attributeChangedCallback() {
      if (this.lifecycle && this.video) this.configure();
    }

    prepare() {
      const duration = this.video.duration;
      if (!Number.isFinite(duration) || duration <= 0) return;
      const ranges = this.video.seekable;
      const seekable = ranges.length > 0 && ranges.end(ranges.length - 1) > 0;
      const changed = duration !== this.duration || seekable !== this.seekable;
      this.duration = duration;
      this.seekable = seekable;
      if (changed) this.configure();
      if (!seekable) this.ensureSeekableSource();
    }

    configure() {
      this.stop();
      const limited = this.motionPreference.matches || this.connection?.saveData;
      if ((limited || this.dataset.motion !== 'true') && this.sourceLoaded) {
        this.releaseBlob();
        this.video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
        this.video.removeAttribute('src');
        this.sourceLoaded = false;
        this.duration = null;
        this.seekable = false;
        this.video.load();
        this.removeAttribute('data-frame-ready');
      }
      const ready = !!this.duration && this.seekable && !this.failed && this.dataset.motion === 'true' && !limited;
      this.toggleAttribute('data-video-ready', ready);
      this.toggleAttribute('data-enhanced', ready && !this.reading);
      if (this.toggle) {
        this.toggle.textContent = this.reading ? this.toggle.dataset.filmLabel : this.toggle.dataset.readLabel;
        this.toggle.setAttribute('aria-pressed', String(this.reading));
      }
      this.video.preload = this.sourceLoaded && !limited ? 'metadata' : 'none';
      this.video.pause();
      this.measureStage();
      this.ensureSource();
      if (ready && !this.reading) this.schedule();
      else {
        this.chapterLinks.forEach((link) => link.removeAttribute('aria-current'));
        this.steps.forEach((step) => step.removeAttribute('aria-current'));
      }
    }

    ensureSource() {
      if (!this.near || this.sourceLoaded || this.failed || this.reading || this.dataset.motion !== 'true' || this.motionPreference.matches || this.connection?.saveData) return;
      this.sourceLoaded = true;
      this.video.querySelectorAll('source[data-film-src]').forEach((source) => { source.src = source.dataset.filmSrc; });
      this.video.preload = 'metadata';
      this.video.load();
    }

    async ensureSeekableSource() {
      if (!this.near || !this.sourceLoaded || this.seekable || this.video.readyState < 3 || this.blobAttempted || this.failed || this.reading || this.dataset.motion !== 'true' || this.motionPreference.matches || this.connection?.saveData) return;
      const source = new URL(this.video.currentSrc, window.location.href);
      // Some static hosts cannot serve byte ranges. A local Blob makes their
      // fully downloaded MP4 seekable, without changing Shopify CDN delivery.
      if (source.origin !== window.location.origin || !source.pathname.toLowerCase().endsWith('.mp4')) return;
      this.blobAttempted = true;
      const controller = new AbortController();
      this.blobController = controller;
      try {
        const response = await fetch(source.href, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Film download failed');
        const file = await response.blob();
        if (controller.signal.aborted || !this.isConnected || this.motionPreference.matches || this.connection?.saveData || this.dataset.motion !== 'true') return;
        if (!file.size) throw new Error('Film is empty');
        this.blobUrl = URL.createObjectURL(new Blob([file], { type: 'video/mp4' }));
        this.duration = null;
        this.seekable = false;
        this.removeAttribute('data-frame-ready');
        this.video.src = this.blobUrl;
        this.video.load();
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) this.fail();
      } finally {
        if (this.blobController === controller) this.blobController = null;
      }
    }

    releaseBlob() {
      this.blobController?.abort();
      this.blobController = null;
      if (this.blobUrl) {
        if (this.video?.getAttribute('src') === this.blobUrl) this.video.removeAttribute('src');
        URL.revokeObjectURL(this.blobUrl);
        this.blobUrl = null;
      }
      this.blobAttempted = false;
    }

    fail() {
      this.failed = true;
      this.removeAttribute('data-frame-ready');
      this.configure();
    }

    frameReady() {
      if (this.video.readyState >= 2 && !this.failed) this.setAttribute('data-frame-ready', '');
    }

    measureStage() {
      const height = Math.ceil(this.stage.getBoundingClientRect().height);
      if (height > 0 && height !== this.stageHeight) {
        this.stageHeight = height;
        this.style.setProperty('--film-stage-height', `${height}px`);
      }
    }

    stop() {
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    schedule() {
      if (this.frame || !this.near || !this.isConnected || !this.hasAttribute('data-enhanced')) return;
      this.frame = window.requestAnimationFrame((now) => this.update(now));
    }

    metrics() {
      const bounds = this.track.getBoundingClientRect();
      const stickyTop = parseFloat(getComputedStyle(this.stage).top) || 0;
      const distance = Math.max(1, this.track.offsetHeight - this.stage.offsetHeight);
      return { bounds, stickyTop, distance };
    }

    jumpTo(index, behavior) {
      const { bounds, stickyTop, distance } = this.metrics();
      const progress = Math.min(.999, index / this.steps.length + (index ? .012 : 0));
      window.scrollTo({ top: window.scrollY + bounds.top - stickyTop + distance * progress, behavior });
    }

    update(now) {
      this.frame = null;
      if (!this.hasAttribute('data-enhanced')) return;
      const { bounds, stickyTop, distance } = this.metrics();
      this.target = Math.max(0, Math.min(1, (stickyTop - bounds.top) / distance));
      const visible = bounds.bottom > 0 && bounds.top < window.innerHeight;
      if (!visible) { this.progress = this.target; return; }
      const delta = this.target - this.progress;
      this.progress = Math.abs(delta) < .0008 ? this.target : this.progress + delta * .22;
      this.style.setProperty('--film-progress', this.progress.toFixed(5));
      const index = Math.min(this.steps.length - 1, Math.floor(this.progress * this.steps.length));
      this.steps.forEach((step, i) => {
        step.classList.toggle('is-active', i === index);
        if (i === index) step.setAttribute('aria-current', 'step');
        else step.removeAttribute('aria-current');
      });
      this.chapterLinks.forEach((link, i) => {
        if (i === index) link.setAttribute('aria-current', 'step');
        else link.removeAttribute('aria-current');
      });
      const time = Math.min(this.progress * this.duration, Math.max(0, this.duration - .045));
      if (this.video.readyState >= 1 && !this.video.seeking && Math.abs(this.video.currentTime - time) > .022) {
        try { this.video.currentTime = time; }
        catch { this.fail(); return; }
      }
      if (Math.abs(this.target - this.progress) > .0008) this.schedule();
    }
  }

  customElements.define('tup-process', TupProcess);
})();
